import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { formatCurrency } from '../utils/currency';
import { Plus, Zap, Trash2, Building2, Receipt } from 'lucide-react';

const Utilities = () => {
    const [tab, setTab] = useState('bills');
    return (
        <div className="max-w-6xl mx-auto space-y-6">
            <div>
                <h1 className="text-2xl font-bold">Utilities</h1>
                <p className="text-gray-600">Utility types, per-property rates, and tenant bills.</p>
            </div>
            <div className="border-b border-gray-200 flex gap-4">
                {[['bills','Bills'],['rates','Property rates'],['types','Utility types']].map(([id,label]) => (
                    <button key={id} onClick={() => setTab(id)}
                        className={`pb-2 px-1 text-sm font-medium border-b-2 ${tab===id ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{label}</button>
                ))}
            </div>
            {tab === 'bills' && <BillsTab />}
            {tab === 'rates' && <RatesTab />}
            {tab === 'types' && <TypesTab />}
        </div>
    );
};

/* ---------- Bills ---------- */

const BillsTab = () => {
    const [bills, setBills] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [types, setTypes] = useState([]);
    const [properties, setProperties] = useState([]);
    const [tenants, setTenants] = useState([]);

    const load = async () => {
        try {
            const [b, t, p, ts] = await Promise.all([
                axios.get('/api/utilities/bills/list'),
                axios.get('/api/utilities'),
                axios.get('/api/properties').catch(() => ({ data: { properties: [] } })),
                axios.get('/api/tenants').catch(() => ({ data: { tenants: [] } })),
            ]);
            setBills(b.data.bills || []); setTypes(t.data.utilities || []);
            setProperties(p.data.properties || []); setTenants(ts.data.tenants || []);
        } catch { toast.error('Failed'); }
    };
    useEffect(() => { load(); }, []);

    const markPaid = async id => {
        try { await axios.post(`/api/utilities/bills/${id}/mark-paid`); toast.success('Marked paid'); load(); }
        catch { toast.error('Failed'); }
    };

    return (
        <>
            <div className="flex justify-end">
                <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg">
                    <Plus className="h-4 w-4" /> New utility bill
                </button>
            </div>
            <div className="bg-white rounded-xl border">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left"><tr>
                        <th className="p-3">Period</th><th className="p-3">Utility</th><th className="p-3">Tenant</th>
                        <th className="p-3">Property</th><th className="p-3 text-right">Usage</th>
                        <th className="p-3 text-right">Amount</th><th className="p-3">Due</th>
                        <th className="p-3">Status</th><th></th>
                    </tr></thead>
                    <tbody>
                        {bills.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-gray-500">No utility bills yet.</td></tr>}
                        {bills.map(b => (
                            <tr key={b.id} className="border-t">
                                <td className="p-3 text-xs">{b.billing_period_start} → {b.billing_period_end}</td>
                                <td className="p-3">{b.utility_name}</td>
                                <td className="p-3">{b.tenant_name}</td>
                                <td className="p-3">{b.property_name}</td>
                                <td className="p-3 text-right">{b.usage_amount ?? '—'}</td>
                                <td className="p-3 text-right">{formatCurrency(b.total_amount)}</td>
                                <td className="p-3">{new Date(b.due_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                                <td className="p-3 capitalize">{b.status}</td>
                                <td className="p-3">{b.status !== 'paid' && <button onClick={() => markPaid(b.id)} className="text-xs px-2 py-1 border rounded">Mark paid</button>}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {showForm && <BillForm types={types} properties={properties} tenants={tenants} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
        </>
    );
};

const BillForm = ({ types, properties, tenants, onClose, onSaved }) => {
    const today = new Date().toISOString().slice(0, 10);
    const [f, setF] = useState({
        property_id:'', tenant_id:'', utility_id:'',
        billing_period_start: today, billing_period_end: today,
        usage_amount:'', rate:'', due_date: today,
    });
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setF(x => ({ ...x, [k]: v }));

    const submit = async e => {
        e.preventDefault(); setSaving(true);
        try { const { data } = await axios.post('/api/utilities/bills', f); toast.success(`Billed ${formatCurrency(data.total_amount)}`); onSaved(); }
        catch (err) { toast.error(err.response?.data?.message || 'Failed'); setSaving(false); }
    };

    const propertyTenants = tenants.filter(t => String(t.property_id) === String(f.property_id));

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
            <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-white rounded-xl max-w-lg w-full shadow-2xl">
                <div className="p-4 border-b flex justify-between"><h2 className="font-semibold">New utility bill</h2><button type="button" onClick={onClose}>✕</button></div>
                <div className="p-4 grid grid-cols-2 gap-3">
                    <F label="Property" span2><select required className="in" value={f.property_id} onChange={e => { set('property_id', e.target.value); set('tenant_id',''); }}>
                        <option value="">Select…</option>{properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select></F>
                    <F label="Tenant" span2><select required className="in" value={f.tenant_id} onChange={e => set('tenant_id', e.target.value)} disabled={!f.property_id}>
                        <option value="">Select…</option>{propertyTenants.map(t => <option key={t.id} value={t.id}>{t.first_name} {t.last_name}</option>)}
                    </select></F>
                    <F label="Utility" span2><select required className="in" value={f.utility_id} onChange={e => set('utility_id', e.target.value)}>
                        <option value="">Select…</option>{types.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select></F>
                    <F label="Period start"><input required type="date" className="in" value={f.billing_period_start} onChange={e => set('billing_period_start', e.target.value)} /></F>
                    <F label="Period end"><input required type="date" className="in" value={f.billing_period_end} onChange={e => set('billing_period_end', e.target.value)} /></F>
                    <F label="Usage"><input type="number" step="0.01" className="in" placeholder="units" value={f.usage_amount} onChange={e => set('usage_amount', e.target.value)} /></F>
                    <F label="Rate"><input required type="number" step="0.0001" className="in" placeholder="per unit / flat" value={f.rate} onChange={e => set('rate', e.target.value)} /></F>
                    <F label="Due date" span2><input required type="date" className="in" value={f.due_date} onChange={e => set('due_date', e.target.value)} /></F>
                </div>
                <div className="p-4 border-t flex justify-end gap-2">
                    <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg">Cancel</button>
                    <button disabled={saving} className="px-4 py-2 bg-primary-600 text-white rounded-lg">Create</button>
                </div>
                <style>{`.in{width:100%;padding:0.5rem 0.75rem;border:1px solid #d1d5db;border-radius:0.5rem}`}</style>
            </form>
        </div>
    );
};
const F = ({ label, children, span2 }) => <div className={span2 ? 'col-span-2' : ''}><label className="block text-sm text-gray-700 mb-1">{label}</label>{children}</div>;

/* ---------- Property rates ---------- */

const RatesTab = () => {
    const [properties, setProperties] = useState([]);
    const [types, setTypes] = useState([]);
    const [propId, setPropId] = useState('');
    const [rates, setRates] = useState([]);
    const [newRate, setNewRate] = useState({ utility_id:'', rate:'', billing_cycle:'monthly' });

    const loadProps = async () => {
        const r = await axios.get('/api/properties'); setProperties(r.data.properties || []);
        const t = await axios.get('/api/utilities'); setTypes(t.data.utilities || []);
    };
    const loadRates = async id => {
        if (!id) return setRates([]);
        const r = await axios.get(`/api/utilities/property/${id}`); setRates(r.data.rates || []);
    };
    useEffect(() => { loadProps(); }, []);
    useEffect(() => { loadRates(propId); }, [propId]);

    const add = async e => {
        e.preventDefault();
        if (!newRate.utility_id || !(Number(newRate.rate) >= 0)) return toast.error('Utility and rate required');
        try { await axios.post(`/api/utilities/property/${propId}`, newRate); toast.success('Added'); setNewRate({ utility_id:'', rate:'', billing_cycle:'monthly' }); loadRates(propId); }
        catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    };
    const remove = async id => {
        try { await axios.delete(`/api/utilities/property/${propId}/${id}`); loadRates(propId); }
        catch { toast.error('Failed'); }
    };

    return (
        <div className="space-y-4">
            <div className="bg-white rounded-xl border p-4 flex gap-3 items-end">
                <div className="flex-1"><label className="text-sm text-gray-500">Property</label>
                    <select className="w-full border rounded-lg px-3 py-2" value={propId} onChange={e => setPropId(e.target.value)}>
                        <option value="">Select…</option>{properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                </div>
            </div>
            {propId && (
                <>
                    <form onSubmit={add} className="bg-white rounded-xl border p-4 flex flex-wrap gap-3 items-end">
                        <div><label className="text-sm text-gray-500">Utility</label>
                            <select className="border rounded-lg px-3 py-2" value={newRate.utility_id} onChange={e => setNewRate({...newRate, utility_id:e.target.value})}>
                                <option value="">Select…</option>{types.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                            </select>
                        </div>
                        <div><label className="text-sm text-gray-500">Rate</label>
                            <input type="number" step="0.0001" className="border rounded-lg px-3 py-2 w-32" value={newRate.rate} onChange={e => setNewRate({...newRate, rate:e.target.value})} />
                        </div>
                        <div><label className="text-sm text-gray-500">Cycle</label>
                            <select className="border rounded-lg px-3 py-2" value={newRate.billing_cycle} onChange={e => setNewRate({...newRate, billing_cycle:e.target.value})}>
                                {['monthly','quarterly','annually'].map(c => <option key={c}>{c}</option>)}
                            </select>
                        </div>
                        <button className="px-4 py-2 bg-primary-600 text-white rounded-lg">Add</button>
                    </form>
                    <div className="bg-white rounded-xl border">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-left"><tr><th className="p-3">Utility</th><th className="p-3">Rate</th><th className="p-3">Cycle</th><th></th></tr></thead>
                            <tbody>{rates.length === 0 ? <tr><td colSpan={4} className="p-6 text-center text-gray-500">No rates set for this property.</td></tr> :
                                rates.map(r => (
                                    <tr key={r.id} className="border-t"><td className="p-3">{r.utility_name}</td><td className="p-3">{r.rate}</td><td className="p-3 capitalize">{r.billing_cycle}</td>
                                        <td className="p-3 text-right"><button onClick={() => remove(r.id)} className="text-red-600 p-1"><Trash2 className="h-4 w-4" /></button></td></tr>
                                ))}</tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

/* ---------- Utility types ---------- */

const TypesTab = () => {
    const [rows, setRows] = useState([]);
    const [f, setF] = useState({ name:'', description:'' });
    const load = async () => { const r = await axios.get('/api/utilities'); setRows(r.data.utilities || []); };
    useEffect(() => { load(); }, []);
    const add = async e => {
        e.preventDefault();
        try { await axios.post('/api/utilities', f); toast.success('Added'); setF({ name:'', description:'' }); load(); }
        catch { toast.error('Failed'); }
    };
    const remove = async id => { await axios.delete(`/api/utilities/${id}`); load(); };
    return (
        <div className="space-y-4">
            <form onSubmit={add} className="bg-white rounded-xl border p-4 flex gap-2 items-end">
                <div className="flex-1"><label className="text-sm text-gray-500">Name</label>
                    <input required className="w-full border rounded-lg px-3 py-2" placeholder="e.g. Solar" value={f.name} onChange={e => setF({...f, name:e.target.value})} /></div>
                <div className="flex-1"><label className="text-sm text-gray-500">Description</label>
                    <input className="w-full border rounded-lg px-3 py-2" value={f.description} onChange={e => setF({...f, description:e.target.value})} /></div>
                <button className="px-4 py-2 bg-primary-600 text-white rounded-lg">Add</button>
            </form>
            <div className="bg-white rounded-xl border">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left"><tr><th className="p-3">Name</th><th className="p-3">Description</th><th></th></tr></thead>
                    <tbody>{rows.map(u => (
                        <tr key={u.id} className="border-t"><td className="p-3">{u.name}</td><td className="p-3 text-gray-600">{u.description || '—'}</td>
                            <td className="p-3 text-right"><button onClick={() => remove(u.id)} className="text-red-600 p-1"><Trash2 className="h-4 w-4" /></button></td></tr>
                    ))}</tbody>
                </table>
            </div>
        </div>
    );
};

export default Utilities;
